"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { fontWeights } from "@/utils/fonts";
import {
    Code2,
    Palette,
    Database,
    Briefcase,
    Bug,
    Server,
    Star,
    ArrowRight,
    User,
    Zap,
    Globe,
    Cpu,
    Shield
} from "lucide-react";

const IconMap: Record<string, any> = {
    Code2,
    Palette,
    Database,
    Briefcase,
    Bug,
    Server,
    Zap,
    Globe,
    Cpu,
    Shield
};

interface Service {
    title: string;
    desc: string;
}

interface Expert {
    name: string;
    title: string;
    skills: string[];
    exp: string;
    rating: number;
    bio: string;
    availability: string;
    icon: string;
}

interface Category {
    id: string;
    name: string;
    icon: string;
    services: Service[];
    expert: Expert;
}

export default function CompleteStaffAugmentation({
    title,
    btnText,
    categories: cmsCategories
}: {
    title?: string;
    btnText?: string;
    categories?: Category[];
}) {
    const [activeTab, setActiveTab] = useState(cmsCategories?.[0]?.id || "");

    // If no categories, render nothing
    if (!cmsCategories || cmsCategories.length === 0) return null;

    // Update active tab if it's no longer valid
    const currentCategory = cmsCategories.find(c => c.id === activeTab) || cmsCategories[0];
    const activeCategory = currentCategory;
    const services = activeCategory.services || [];
    const expert = activeCategory.expert || {
        name: "Expert",
        title: "Expert",
        skills: [],
        exp: "N/A",
        rating: 5,
        bio: "",
        availability: "Available Now",
        icon: "User"
    };

    return (
        <section className="w-full overflow-hidden py-3 tsm-350:py-3 tsm-400:py-4 md:py-[25px] lg:py-[30px] bg-[#F4F4F4] relative">
            <div className="px-4 sm:px-8 md:px-0 lg:max-w-[1220px] xl:max-w-[1342px] mx-auto relative z-10">
                {/* Header */}
                <div className="text-center mb-4 tsm-400:mb-[20px] md:mb-[30px] lg:mb-[35px] xl:mb-[50px]">
                    <h2
                        className="text-[22px] sm:text-[32px] md:text-[35px] lg:text-[48px] xl:text-[60px] text-[#0F172A] leading-tight"
                        style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.regular }}
                        dangerouslySetInnerHTML={{ __html: title || '' }}
                    />

                    {/* Category Tabs */}
                    <div className="grid grid-cols-1 tsm-350:grid-cols-2 tsm-400:grid-cols-3 mt-[16px] sm:mt-[22px] lg:mt-[24px] xl:mt-[38px] md:flex md:flex-wrap md:justify-center gap-1 tsm-400:gap-2 lg:gap-4">
                        {cmsCategories.map((cat) => {
                            const Icon = IconMap[cat.icon] || Code2;
                            const isActive = activeTab === cat.id;

                            return (
                                <button
                                    key={cat.id}
                                    onClick={() => setActiveTab(cat.id)}
                                    className={`rounded-[4px] leading-[16px] transition-colors duration-0 lg:leading-[20px] xl:leading-[24px] w-full md:w-auto flex items-center 
                                        gap-2 
                                        tsm-400:gap-1 
                                        sm:gap-2
                                        md:gap-1 
                                        lg:gap-2    
                                        px-2.5 
                                        md:px-3 
                                        lg:px-4 
                                        xl:px-6    
                                        py-1.5 
                                        md:py-2 
                                        lg:py-3 
                                        xl:py-4   
                                        h-9 
                                        sm:h-[40px] 
                                        lg:h-[40px] 
                                        xl:h-[46px]
                                        text-[11px] 
                                        tsm-400:text-[10px] 
                                        sm:text-[14px] 
                                        md:text-[12px] 
                                        lg:text-[14px] 
                                        xl:text-[16px] 
                                        ${isActive ? "bg-[#002F6D] text-white" : "bg-white text-[#1A2B4A] border border-[#E2E8F0] hover:border-[#5499FF]"}
                                    `}
                                    style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.bold }}
                                >
                                    <Icon size={isActive ? 20 : 18} className="w-3 sm:w-3.5 lg:w-4 xl:w-auto" />
                                    {cat.name}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Content Grid */}
                <div className="flex flex-col lg:flex-row w-full lg:max-w-[1220px] xl:max-w-[1342px] mx-auto gap-4 tsm-400:gap-6 lg:gap-8 xl:gap-10 2xl:gap-20 2xl:gap-[92px] items-stretch justify-center min-h-[615px] xl:min-h-[636px]">
                    {/* Left Column: Services List */}
                    <div className="flex-1 w-full xl:max-w-auto   max-w-auto lg:max-w-[468px] xl:max-w-[540px] 2xl:max-w-[642px] flex flex-col gap-1 sm:gap-2 lg:gap-2 xl:gap-3">
                        {services.map((item, idx) => (
                            <div
                                key={idx}
                                className="rounded-[4px] border border-[#E2E8F0] bg-white hover:shadow-[0px_20px_50px_rgba(0,47,109,0.12)] transition-all duration-300 group
                                  py-3
                                  sm:py-4 
                                  lg:py-[18px] 
                                  xl:py-[21px]
                                  px-3
                                  sm:px-4 
                                  lg:px-[20px] 
                                  xl:px-[23px] 
                                  sm:h-[112px]
                                  lg:h-[118px]
                                  mx-0
                                  sm:mx-8
                                  lg:mx-0
                                  w-auto 
                                  2xl:w-[642px]"
                            >
                                <h3
                                    className="text-[14px] sm:text-[16px] text-[#002F6D] font-bold mb-2 transition-colors"
                                    style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.extrabold }}
                                >
                                    {item.title}
                                </h3>
                                <p
                                    className="text-[13px] sm:text-[14px] text-[#5A6E8C] leading-normal tsm-400:eading-relaxed"
                                    style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.regular }}
                                >
                                    {item.desc}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Right Column: Expert Profile Card */}
                    <div className="flex-1 lg:flex-none relative h-full flex items-center justify-center lg:items-start lg:justify-start pt-0 lg:pt-[9px]
                            w-full 
                            lg:w-[460px] 
                            xl:w-[608px] 
                            px-6 
                            max-[349px]:px-4 
                            md:px-10 
                            lg:px-[40px] 
                            xl:px-[63px] 
                            pb-[20px] 
                            md:pb-[30px] 
                            lg:pb-[40px] 
                            xl:pb-[63px] 
                            ">
                        {/* Decorative background box */}
                        <div className="block absolute top-0 left-1/2 lg:left-0 -translate-x-1/2 translate-y-[40px] max-[349px]:w-[calc(100vw-32px)] bg-[#E9EEF6]
                        w-[300px] 
                        tsm-350:w-[320px] 
                        tsm-400:w-[368px] 
                        sm:w-[500px] 
                        md:w-[500px] 
                        lg:w-[460px] 
                        xl:w-[608px] 
                        h-full
                        tsm-350:h-[430px] 
                        tsm-400:h-[440px] 
                        sm:h-[500px] 
                        md:h-[550px] 
                        lg:h-[550px] 
                        xl:h-[566px] 
                        lg:translate-x-0 
                        sm:translate-y-[40px] 
                        lg:translate-y-[59px] 
                        xl:translate-y-[63px] 
                        " />
                        <div className="w-full max-[349px]:max-w-none max-w-auto tsm-350:max-w-[270px] tsm-400:max-w-[320px] sm:max-w-[420px] lg:max-w-[380px] xl:max-w-none xl:w-full min-h-0 sm:min-h-[500px] md:min-h-[555px] bg-white rounded-[4px] border border-[#E2E8F0] shadow-[0_20px_50px_rgba(0,47,109,0.08)]   p-4 tsm-400:p-5 sm:p-6 md:p-8 xl:p-10 relative flex flex-col">
                            {/* Profile Header */}
                            <div className="flex items-center gap-[16px] mb-4 sm:mb-5 min-h-[72px] max-[349px]:flex-col max-[349px]:items-center max-[349px]:text-center">
                                <div className="w-[60px] h-[60px] md:w-[74px] md:h-[74px] bg-[#002F6D] rounded-full flex items-center justify-center flex-shrink-0">
                                    {(() => {
                                        const ExpertIcon = IconMap[expert.icon] || User;
                                        return <ExpertIcon size={30} className="text-white" />;
                                    })()}
                                </div>
                                <div className="flex flex-col space-y-0 sm:space-y-1 max-[349px]:items-center">
                                    <h4 className=" text-[15px] tsm-400:text-[15px] sm:text-[16px] md:text-[20px] text-[#002F6D]"
                                        style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.extrabold }}
                                    >{expert.name || ""}</h4>
                                    <p className="text-[13px] tsm-400:text-[14px] md:text-[16px] text-[#5A6E8C]"
                                        style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.regular }}
                                    >{expert.title}</p>
                                    <div className="flex items-center gap-1">
                                        {[1, 2, 3, 4, 5].map((s) => (
                                            <Star key={s} size={14} fill={s <= Math.round(expert.rating) ? "#F9BB01" : "none"} stroke="#F9BB01" />
                                        ))}
                                        <span className="text-[12px] sm:text-[13px] font-bold text-[#002F6D] ml-1">{expert.rating}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Tech Tags */}
                            <div className="flex flex-wrap gap-[7px] mb-[22px]">
                                {(expert.skills || []).map((tag) => (
                                    <span key={tag} className="bg-[#E8F0FE] text-[#002F6D] text-[10px] tsm-400:text-[11px] md:text-[12px] px-[13px] py-1 tsm-400:py-1.5 sm:py-[7.5px] rounded-full font-medium"
                                        style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.medium }}
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>

                            {/* Details & Status */}
                            <div className="flex items-center justify-between py-2 tsm-400:py-3 sm:py-4 mb-4 sm:mb-[22px] border-y border-[#E2E8F0]">
                                <div className="flex items-center gap-2 text-[#5A6E8C] text-[13px] sm:text-[14px]"
                                    style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.semiBold }}
                                >
                                    <Briefcase size={18} />
                                    {expert.exp}
                                </div>
                                <div className="flex items-center gap-2 text-[#00A63E] text-[13px] sm:text-[14px]"
                                    style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.medium }}
                                >
                                    <span className="w-2.5 h-2.5 rounded-full bg-[#00A63E]"></span>
                                    {expert.availability || ""}
                                </div>
                            </div>

                            {/* Bio */}
                            <p className="text-[#5A6E8C] text-[12px] md:text-[15px] leading-relaxed mb-10 h-auto sm:h-[80px] leading-[20px] sm:leading-[25px]">
                                {expert.bio}
                            </p>

                            {/* CTA Button */}
                            <button
                                className="relative sm:absolute mt-auto bg-[#002F6D] hover:bg-[#002F6D]/90 text-white rounded-[6.7px] flex items-center justify-center gap-3 transition-all
                                sm:left-6 
                                md:left-8 
                                xl:left-10 
                                sm:right-6 
                                md:right-8 
                                xl:right-10
                                py-2
                                tsm-400:py-2
                                sm:py-[12px]
                                lg:py-[18px] 
                                xl:py-[21px]
                                px-4
                                tsm-400:px-4
                                sm:px-[20px]
                                lg:px-[80px] 
                                xl:px-[132px] 
                                h-[40px]
                                tsm-400:h-[40px]
                                sm:h-[45px] 
                                lg:h-[45px] 
                                xl:h-[59px] 
                                text-[13px] 
                                sm:text-[14px] 
                                md:text-[16px] 
                                sm:bottom-[24px] 
                                md:bottom-[32px]
                                xl:bottom-[40px]
                                "
                                style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.semiBold }}
                                onClick={() => window.dispatchEvent(new CustomEvent("openContactPopup"))}
                            >
                                {btnText || ""} <ArrowRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
